// src/service/auth.ts
import API from "../lib/axios";  // ✅ Correct path
import { User } from "../types";

export async function loginUser(email: string, password: string): Promise<User> {
  const res = await API.post("/api/v1/auth/login", { email, password });
  const response = res.data;

  if (response.success && response.data) {
    const { user, tokens } = response.data;
    
    if (tokens.accessToken) {
      localStorage.setItem("authToken", tokens.accessToken);
      // DEBUG: Verify token was stored
      console.log('✅ Token stored in localStorage:', localStorage.getItem("authToken") ? 'Yes' : 'No');
    }

    // Transform backend user format to frontend format
    const frontendUser: User = {
      id: user.id,
      email: user.email,
      name: user.name,
      token: tokens.accessToken,
      role: user.role.name,
      roleId: user.role.id,
      permissions: user.permissions || [], // Store permissions array
      hierarchy: user.hierarchy, // Include hierarchy information
    };

    return frontendUser;
  } else {
    throw new Error(response.message || "Login failed");
  }
}

export async function logoutUser() {
  const token = localStorage.getItem("authToken");
  
  // Always clear local storage first, even if API call fails
  // This ensures user can logout even if backend is down
  localStorage.removeItem("authToken");
  localStorage.removeItem("user");
  localStorage.removeItem("role");
  
  // Try to call logout API, but don't fail if it errors
  if (token) {
    try {
      await API.post(
        "/api/v1/auth/logout",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      // Log error but don't throw - logout should succeed even if API fails
      console.warn("Logout API call failed, but local session cleared:", error);
    }
  }
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string
): Promise<{ success: boolean; message: string }> {
  // Note: Authorization header is automatically added by axios interceptor
  // If no token exists, the backend will return 401 and the interceptor will handle it
  try {
    // Verify token exists before making request (for better error message)
    if (typeof window !== "undefined") {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("You must be logged in to change your password. Please log in and try again.");
      }
    }

      // Use relative path to go through Next.js API route proxy
      // This ensures the Authorization header is properly forwarded to backend
      const res = await API.post(
        "/api/v1/auth/change-password",
        {
          currentPassword,
          newPassword,
          confirmPassword,
        }
      );

    // Backend returns: { success: true, message: '...' }
    if (res.data && res.data.success) {
      return {
        success: true,
        message: res.data.message || "Password changed successfully",
      };
    }

    // If response doesn't have success field, check for error message
    throw new Error(res.data?.message || "Failed to change password");
  } catch (error: unknown) {
    // Handle axios errors
    if (error && typeof error === "object" && "response" in error) {
      const axiosError = error as { response?: { data?: { message?: string; code?: string } } };
      const errorMessage = axiosError.response?.data?.message || "Failed to change password";
      const errorCode = axiosError.response?.data?.code;
      
      // Create error with code for better error handling
      const err = new Error(errorMessage) as Error & { code?: string };
      err.code = errorCode;
      throw err;
    }
    
    // Re-throw if it's already an Error
    if (error instanceof Error) {
      throw error;
    }
    
    // Fallback for unknown error types
    throw new Error("Failed to change password");
  }
}

export async function forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
  const res = await API.post("/api/v1/auth/forgot-password", { email });
  
  if (!res.data.success) {
    throw new Error(res.data.message || "Failed to send password reset email");
  }

  return res.data;
}

export async function verifyResetToken(
  token: string
): Promise<{ success: boolean; message: string; data?: { valid: boolean; expires_at: string } }> {
  const res = await API.get("/api/v1/auth/verify-reset-token", {
    params: { token },
  });

  if (!res.data.success) {
    throw new Error(res.data.message || "Invalid or expired token");
  }

  return res.data;
}

export async function resetPassword(
  resetToken: string,
  newPassword: string,
  confirmPassword: string
): Promise<{ success: boolean; message: string; data?: { password_changed_at: string; next_password_expiry: string } }> {
  const res = await API.post("/api/v1/auth/reset-password", {
    reset_token: resetToken,
    new_password: newPassword,
    confirm_password: confirmPassword,
  });

  if (!res.data.success) {
    throw new Error(res.data.message || "Failed to reset password");
  }

  return res.data;
}